import * as yup from "yup";
import { useFormik } from "formik";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, OutlinedInput } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";

import Title from "../../common/Title";

import useAlert from "../../../hooks/useAlert";
import useAxios from "../../../hooks/useAxios";
import { CLIENTS } from "../../../constants/value";
import { __getCredential, __updateCredential } from "../../../api/sim";

const CredentialForm = () => {
  const [credential, setCredential] = useState(null);
  const [formData, setFormData] = useState(null);

  const { setAlertMessage } = useAlert();
  const [response, call, loading] = useAxios({
    ...__getCredential(CLIENTS.ultra),
  });
  const [updateResponse, updateCall, updateLoading] = useAxios({
    ...__updateCredential(CLIENTS.ultra),
  });
  useEffect(() => {
    call();
  }, []);

  useEffect(() => {
    if (formData) {
      updateCall({
        data: formData,
      });
    }
  }, [formData]);

  useEffect(() => {
    if (!updateResponse) return;
    if (updateResponse.status) {
      setCredential(updateResponse.data);
      setAlertMessage({ type: "success", message: "Update successful" });
    }
  }, [updateResponse]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setCredential(response.data);
      formik.setValues({
        username: response.data.username,
        password: response.data.password,
        api_key: response.data.api_key,
        secret_key: response.data.secret_key,
        callback_url: response.data.callback_url
      });
    }
  }, [response]);

  const initialData = {
    username: "",
    password: "",
    api_key: "",
    secret_key: "",
    callback_url: "",
  };

  const validationSchema = yup.object({
    username: yup.string("username"),
    password: yup.string("password"),
    api_key: yup.string("api_key"),
    secret_key: yup.string("secret_key"),
    callback_url: yup.string("callback_url").url(),
  });

  const formik = useFormik({
    initialValues: { ...initialData },
    onSubmit: (values) => {
      const requestData = Object.keys(values).reduce((obj, key) => {
        obj[key] = values[key] || null;
        return obj;
      }, {});

      setFormData(requestData);
    },
    validationSchema: validationSchema,
    validateOnChange: true,
  });

  if (loading || updateLoading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Ultra Credentials"
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1, width: "100%" }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs sx={{ minWidth: 400 }}>
            <FormControl
              error={
                formik.touched.username && Boolean(formik.errors.username)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="username">Username</InputLabel>
              <OutlinedInput
                name="username"
                label="Username"
                id="username"
                value={formik.values.username}
                onChange={formik.handleChange}
              />
              {formik.touched.username && Boolean(formik.errors.username) ? (
                <FormHelperText id="username-error-text">
                  {formik.errors.username}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs sx={{ minWidth: 400 }}>
            <FormControl
              error={
                formik.touched.password && Boolean(formik.errors.password)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="password">Password</InputLabel>
              <OutlinedInput
                name="password"
                label="Password"
                id="password"
                value={formik.values.password}
                onChange={formik.handleChange}
              />
              {formik.touched.password && Boolean(formik.errors.password) ? (
                <FormHelperText id="password-error-text">
                  {formik.errors.password}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs sx={{ minWidth: 400 }}>
            <FormControl
              error={
                formik.touched.api_key && Boolean(formik.errors.api_key)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="api_key">Api Key</InputLabel>
              <OutlinedInput
                name="api_key"
                label="Api Key"
                id="api_key"
                value={formik.values.api_key}
                onChange={formik.handleChange}
              />
              {formik.touched.api_key && Boolean(formik.errors.api_key) ? (
                <FormHelperText id="api_key-error-text">
                  {formik.errors.api_key}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs sx={{ minWidth: 400 }}>
            <FormControl
              error={
                formik.touched.secret_key && Boolean(formik.errors.secret_key)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="secret_key">Secret Key</InputLabel>
              <OutlinedInput
                name="secret_key"
                label="Secret Key"
                id="secret_key"
                value={formik.values.secret_key}
                onChange={formik.handleChange}
              />
              {formik.touched.secret_key &&
              Boolean(formik.errors.secret_key) ? (
                <FormHelperText id="secret_key-error-text">
                  {formik.errors.secret_key}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs sx={{ minWidth: 400 }}>
            <FormControl
              error={
                formik.touched.callback_url && Boolean(formik.errors.callback_url)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="callback_url">Callback URL</InputLabel>
              <OutlinedInput
                name="callback_url"
                label="Callback URL"
                id="callback_url"
                value={formik.values.callback_url}
                onChange={formik.handleChange}
              />
              {formik.touched.callback_url &&
              Boolean(formik.errors.callback_url) ? (
                <FormHelperText id="callback_url-error-text">
                  {formik.errors.callback_url}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={
                  loading || updateLoading
                }
                onClick={() =>
                  !loading && !updateLoading && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Update"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default CredentialForm;
